import { useMutation, useQuery } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { resolveApiMessage, showToast } from '../../../helpers';
import { NAVIGATE_PATH as PATH } from '../../../helpers/constants';
import {
  getMyCatalogue,
  getMyEvent,
  requestMyChange,
  requestMyCourse,
} from '../../../requests/customerPortal';
import {
  btnPrimary,
  btnSecondary,
  Card,
  inputClass,
  labelClass,
  PageBody,
  PageHeader,
} from '../../Admin/ui';
import {
  dateInputClass,
  fmtDateTime,
  LoadFailed,
  retryUnless4xx,
} from '../shared';

const CATEGORIES = [
  {
    value: 'Business Application',
    label: 'Business Application',
    hint: 'Technical skills — Microsoft, Adobe, Google, AI tools',
  },
  {
    value: 'Professional Development',
    label: 'Professional Development',
    hint: 'Human skills — leadership, communication, management',
  },
];

const FORMATS = [
  {
    value: 'Onsite',
    label: 'Onsite',
    hint: 'A trainer comes to your location',
  },
  {
    value: 'Live Online Training',
    label: 'Live Online Training',
    hint: 'Virtual, instructor-led',
  },
];

const Choice: React.FC<{
  options: { value: string; label: string; hint: string }[];
  value: string;
  onChange: (v: string) => void;
}> = ({ options, value, onChange }) => (
  <div className="grid gap-2 sm:grid-cols-2">
    {options.map((o) => (
      <button
        type="button"
        key={o.value}
        onClick={() => onChange(o.value)}
        className={`rounded-xl border px-4 py-3 text-left transition-colors ${
          value === o.value
            ? 'border-blue-primary bg-blue-100'
            : 'border-white-regular bg-white hover:bg-grey-50'
        }`}
      >
        <div className="text-sm font-semibold text-gray-five">{o.label}</div>
        <div className="text-xs text-gray-bold">{o.hint}</div>
      </button>
    ))}
  </div>
);

/**
 * "Ask for a date or time change" — the event is already on the account,
 * so the request goes to the scheduler linked to that exact class rather
 * than as a free-text ticket to be matched by hand.
 */
const ChangeRequest: React.FC<{ eventId: string }> = ({ eventId }) => {
  const [requestedDate, setRequestedDate] = useState('');
  const [requestedTime, setRequestedTime] = useState('');
  const [notes, setNotes] = useState('');
  const [done, setDone] = useState<string | null>(null);
  const {
    data: event,
    isPending,
    error,
  } = useQuery({
    retry: retryUnless4xx,
    queryKey: ['my', 'event', eventId],
    queryFn: () => getMyEvent(eventId),
  });
  const submit = useMutation({
    mutationFn: requestMyChange,
    onSuccess: (r) => setDone(r.number),
    onError: (e) => showToast(resolveApiMessage(e), 'error'),
  });
  const ready = requestedDate || requestedTime || notes.trim();

  if (isPending) return null;
  if (error || !event) return <LoadFailed error={error} />;

  if (done) {
    return (
      <>
        <PageHeader title="Thank you — we have your change request" />
        <PageBody>
          <Card>
            <div className="px-5 py-6 text-sm text-gray-three">
              <p>
                Our scheduling team will check instructor availability for the
                new time and confirm with you. Your reference is{' '}
                <span className="font-medium text-gray-five">{done}</span>.
              </p>
              <div className="mt-4 flex gap-2">
                <Link to={PATH.adminTickets} className={btnSecondary}>
                  Track it under Tickets
                </Link>
                <Link
                  to={`${PATH.adminEvents}?id=${eventId}`}
                  className={btnSecondary}
                >
                  Back to the event
                </Link>
              </div>
            </div>
          </Card>
        </PageBody>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Ask for a date or time change"
        meta="Tell us what should move. The request reaches the scheduler already linked to this event."
        back={{
          to: `${PATH.adminEvents}?id=${eventId}`,
          label: event.name ?? 'The event',
        }}
      />
      <PageBody>
        <form
          className="grid max-w-3xl gap-6"
          onSubmit={(e) => {
            e.preventDefault();
            if (!ready) return;
            submit.mutate({
              eventId,
              requestedDate: requestedDate || undefined,
              requestedTime: requestedTime || undefined,
              notes: notes.trim() || undefined,
            });
          }}
        >
          <Card title="The event as scheduled">
            <div className="grid gap-1 px-5 py-4 text-sm">
              <div className="font-semibold text-gray-five">{event.name}</div>
              <div className="text-gray-three">
                {fmtDateTime(event.start)}
                {event.instructor ? ` · ${event.instructor}` : ''}
                {event.location ? ` · ${event.location}` : ''}
              </div>
            </div>
          </Card>
          <Card title="What should change?">
            <div className="grid gap-4 px-5 py-4 sm:grid-cols-2">
              <div>
                <label className={labelClass}>New date (optional)</label>
                <input
                  type="date"
                  className={`${dateInputClass} w-full`}
                  value={requestedDate}
                  min={new Date().toISOString().slice(0, 10)}
                  onChange={(e) => setRequestedDate(e.target.value)}
                />
              </div>
              <div>
                <label className={labelClass}>New start time (optional)</label>
                <input
                  type="time"
                  className={`${dateInputClass} w-full`}
                  value={requestedTime}
                  onChange={(e) => setRequestedTime(e.target.value)}
                />
              </div>
              <div className="sm:col-span-2">
                <label className={labelClass}>Notes</label>
                <textarea
                  className={`${inputClass} h-28 w-full py-2`}
                  maxLength={3000}
                  placeholder="Alternative dates that work, why the change, anyone we should copy…"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </div>
            </div>
          </Card>
          <div>
            <button
              type="submit"
              className={btnPrimary}
              disabled={!ready || submit.isPending}
            >
              {submit.isPending ? 'Sending…' : 'Send the request'}
            </button>
          </div>
        </form>
      </PageBody>
    </>
  );
};

const AdminRequestCourse: React.FC = () => {
  const [params] = useSearchParams();
  const changeEventId = params.get('change');
  if (changeEventId) return <ChangeRequest eventId={changeEventId} />;
  return <NewCourseRequest />;
};

const NewCourseRequest: React.FC = () => {
  const [category, setCategory] = useState('');
  const [format, setFormat] = useState('');
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [productId, setProductId] = useState('');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('09:00');
  const [notes, setNotes] = useState('');
  const [done, setDone] = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setQ(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);
  useEffect(() => setProductId(''), [category]);

  const { data: catalogue } = useQuery({
    queryKey: ['my', 'catalogue', category, q],
    queryFn: () => getMyCatalogue(category, q || undefined),
    enabled: Boolean(category),
  });

  const submit = useMutation({
    mutationFn: requestMyCourse,
    onSuccess: (r) => setDone(r.number),
    onError: (e) => showToast(resolveApiMessage(e), 'error'),
  });

  const product = catalogue?.items.find((p) => p.id === productId);
  const ready = category && format && productId && date && time;
  const step = !category ? 1 : !format ? 2 : !productId ? 3 : 4;

  if (done) {
    return (
      <>
        <PageHeader title="Thank you for requesting a new course!" />
        <PageBody>
          <Card>
            <div className="px-5 py-6 text-sm text-gray-three">
              <p>
                Our team will review your request, verify instructor
                availability, and contact you to finalise the logistics. Your
                reference is{' '}
                <span className="font-medium text-gray-five">{done}</span>.
              </p>
              <div className="mt-4 flex gap-2">
                <Link to={PATH.adminTickets} className={btnSecondary}>
                  Track it under Tickets
                </Link>
                <button
                  className={btnSecondary}
                  onClick={() => {
                    setDone(null);
                    setProductId('');
                    setDate('');
                    setNotes('');
                  }}
                >
                  Request another
                </button>
              </div>
            </div>
          </Card>
        </PageBody>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Request a private course"
        meta="Submit a private workshop request in under two minutes — no email chain required."
      />
      <PageBody>
        <form
          className="grid max-w-3xl gap-6"
          onSubmit={(e) => {
            e.preventDefault();
            if (!ready) return;
            submit.mutate({
              category,
              format,
              productId,
              date,
              time,
              notes: notes || undefined,
            });
          }}
        >
          <Card title="1 · Course category">
            <div className="px-5 py-4">
              <Choice
                options={CATEGORIES}
                value={category}
                onChange={setCategory}
              />
            </div>
          </Card>

          {step >= 2 && (
            <Card title="2 · Delivery format">
              <div className="px-5 py-4">
                <Choice options={FORMATS} value={format} onChange={setFormat} />
              </div>
            </Card>
          )}

          {step >= 3 && (
            <Card title="3 · Pick the course">
              <div className="px-5 py-4">
                <input
                  className={`${inputClass} w-full`}
                  placeholder="Type to filter the course list"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <div className="mt-3 max-h-72 overflow-y-auto rounded-lg border border-white-regular">
                  {!catalogue ? (
                    <div className="px-4 py-3 text-sm text-gray-bold">
                      Loading…
                    </div>
                  ) : catalogue.items.length === 0 ? (
                    <div className="px-4 py-3 text-sm text-gray-bold">
                      No course matches. Describe what you need in the notes and
                      we will find the right one.
                    </div>
                  ) : (
                    catalogue.items.map((p) => (
                      <button
                        type="button"
                        key={p.id}
                        onClick={() => setProductId(p.id)}
                        className={`block w-full border-b border-white-three px-4 py-2.5 text-left text-sm last:border-b-0 ${
                          productId === p.id
                            ? 'bg-blue-100 font-semibold text-blue-primary'
                            : 'text-gray-four hover:bg-grey-50'
                        }`}
                      >
                        {p.name}
                        {p.duration && (
                          <span className="ml-2 text-xs font-normal text-gray-bold">
                            {p.duration}
                          </span>
                        )}
                      </button>
                    ))
                  )}
                </div>
                {product && (
                  <div className="mt-2 text-xs text-gray-bold">
                    Selected:{' '}
                    <span className="font-medium text-gray-five">
                      {product.name}
                    </span>
                  </div>
                )}
              </div>
            </Card>
          )}

          {step >= 4 && (
            <Card title="4 · When">
              <div className="grid gap-4 px-5 py-4 sm:grid-cols-2">
                <div>
                  <label className={labelClass}>Date</label>
                  <input
                    type="date"
                    required
                    className={`${dateInputClass} w-full`}
                    value={date}
                    min={new Date().toISOString().slice(0, 10)}
                    onChange={(e) => setDate(e.target.value)}
                  />
                </div>
                <div>
                  <label className={labelClass}>Available time</label>
                  <input
                    type="time"
                    required
                    className={`${dateInputClass} w-full`}
                    value={time}
                    onChange={(e) => setTime(e.target.value)}
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className={labelClass}>Notes</label>
                  <textarea
                    className={`${inputClass} h-28 w-full py-2`}
                    maxLength={3000}
                    placeholder="Trainer preferences, group details, contacts, alternative dates, participant count…"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                  />
                </div>
              </div>
            </Card>
          )}

          {step >= 4 && (
            <div>
              <button
                type="submit"
                className={btnPrimary}
                disabled={!ready || submit.isPending}
              >
                {submit.isPending ? 'Sending…' : 'Next'}
              </button>
            </div>
          )}
        </form>
      </PageBody>
    </>
  );
};

export default AdminRequestCourse;
